import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';

import { Layout, Spin, Row, Col, Card, Table, Button } from 'antd';
import {
	floatToPercentageString,
	getFilterIds,
	getObjectChildrenKeyIds,
	getSpendAnalyticsDrillDownLink,
} from '../../utils/DataFormatterUtils';

import {
	collectAllParentsFromTreeData,
	collectObjectsWithValidPropFromTreeData,
	filterTreeData,
	flattenTreeData,
	nullSafeGet,
	nullSafeGetOrElse,
	removeEmptyChildrenFromTreeData,
	sortTreeData,
} from '../../utils/DataAccessUtils';
import { applyLocalAnalyticsTreeFilters } from '../../utils/AnalyticsTreeFilterUtils';
import AnalyticsBreakdownChartSection from '../analytics_breakdown_chart/AnalyticsBreakdownChartSection';
import { fetchWorkBreakdownComparisonSilent } from '../../thunks/analytics_breakdown_comparison_thunks';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { withRouter } from 'react-router';
import AnalyticsControlBar from '../analytics_control_bar/AnalyticsControlBar';
import {
	clearWorkLocationFilters,
	updateWorkLocationFilters,
} from '../../actions/work_analytics_actions';
import { EmptyState } from '../empty_state/EmptyState';
import {
	fetchWorkOrderCountByLocationWithRegions,
	fetchWorkOrderCountByMonth,
} from '../../thunks/work_analytics_thunks';
import {
	workOrderPrioritiesRestCrudThunksForBuyer,
	workOrderPrioritiesRestCrudThunksForSupplier,
} from '../../thunks/work_order_priorities_thunks';
import { CSV_EXPORT_DEFAULTS, FILTER_CONFIG_NAMES, ROLE_TYPES } from '../../utils/DataConstants';
import { ExportToCsv } from 'export-to-csv';
import { INVOICE_STATUS_DISPLAY_NAMES, INVOICE_STATUSES } from '../../constants/InvoiceStatuses';
import { Link } from 'react-router-dom';
import { withUserType } from '../withUserType';
import { exportAnalyticsPageAsPdf } from '../../utils/AnalyticsPdfExportUtils';

const DataSet = require('@antv/data-set');
const { Content } = Layout;
const workOrderCountByLocationChartConfig = {
	xAxisName: 'name',
	yAxisName: 'subTotal',
	position: 'name*subTotal',
	fillArea: false,
	color: 'name',
	cols: {
		fieldName: { alias: 'Location' },
		subTotal: { alias: '# Work Orders' },
	},
	yAxisLabel: {
		textStyle: {
			fontSize: '14',
			fontFamily: 'Roboto',
			fontWeight: 400,
			fill: 'rgba(0,0,0,0.65)',
		},
	},
	xAxisLabel: {
		textStyle: {
			fontSize: '14',
			fontFamily: 'Roboto',
			fontWeight: 400,
			fill: 'rgba(0,0,0,0.65)',
		},
	},
	transformer: (data) => {
		const ds = new DataSet();
		const dv = ds.createView().source(data);
		dv.transform({
			type: 'sort-by',
			fields: ['subTotal'],
			order: 'ASC', // default is ASC,DESC is reversed order.
		});
		return dv;
	},
};

const defaultSortOrder: 'ascend' | 'descend' = 'descend';
const tooltipTemplate = `
	<li data-index={index}>
			<!-- The marker for each record -->
			<span class="analytics-breakdown-tooltip-marker" style="background-color:{color};"></span>
			{name}: {value} work orders 
	</li>`;

const CSV_TITLE = 'Work Analytics by Locations';

const exportReduceFunction = (obj) => ({
	name: obj.name,
	total: nullSafeGetOrElse('workOrderCounts.subTotal', obj, 0),
	percentage: floatToPercentageString(
		nullSafeGetOrElse('workOrderCounts.percentageOfTotal', obj, 0) / 100
	),
});

const WorkAnalyticsOverviewLocationsPage: FC<any> = ({
	fetchWorkOrderCountsByLocation,
	analytics,
	saveLocationFilters,
	clearAllLocationFilters,
	accountActivated,
	userType,
}): React.ReactElement => {
	const [initialLoading, setInitialLoading] = useState(true);
	const [search, setSearch] = useState('');
	const [filteredTreeData, setFilteredTreeData] = useState([]);
	const [locationIds, setLocationIds] = useState([]);

	useEffect(() => {
		if (analytics.locationFilters.startDate && analytics.locationFilters.endDate) {
			fetchWorkOrderCountsByLocation(analytics.locationFilters).then(() =>
				setInitialLoading(false)
			);
		}
	}, [analytics.locationFilters]);

	const { workOrderCountByLocation, locationFilters } = analytics;

	const filterByLocationIds = useCallback(
		(allData) => applyLocalAnalyticsTreeFilters(allData, { selectedIds: locationIds }),
		[locationIds]
	);

	const woCountByLocationDisplayData = useMemo(() => {
		const woCountByLocation = nullSafeGetOrElse('workOrderCountByLocation', analytics, []);

		const displayData = !!search
			? filteredTreeData
			: filterByLocationIds(nullSafeGetOrElse('data', woCountByLocation, []));
		return sortTreeData(
			displayData,
			(a, b) =>
				parseFloat(nullSafeGetOrElse('workOrderCounts.subTotal', b, 0)) -
				parseFloat(nullSafeGetOrElse('workOrderCounts.subTotal', a, 0))
		);
	}, [analytics, filterByLocationIds, filteredTreeData, search]);

	const onSearch = useCallback(
		(e) => {
			const woCountsByLocation = nullSafeGetOrElse('workOrderCountByLocation.data', analytics, []);

			const search = e.target.value;
			const allData = filterByLocationIds(woCountsByLocation);

			setFilteredTreeData(
				!!search
					? filterTreeData(allData, (obj) => obj.name.toLowerCase().includes(search.toLowerCase()))
					: []
			);
			setSearch(search);
		},
		[analytics, filterByLocationIds]
	);

	const removeLocationIdsIdsAndUpdateFilter = (filters) => {
		const { locationIds, ...otherFilters } = filters;
		setLocationIds(locationIds || []);
		saveLocationFilters(otherFilters);
	};

	const handlePrint = useCallback(
		() =>
			exportAnalyticsPageAsPdf(
				'.analyticsOverviewLocationsPage',
				'work-analytics-locations-' + new Date().toISOString().slice(0, 10) + '.pdf'
			),
		[]
	);

	const workOrderCountByLocationData = useMemo(
		() =>
			collectObjectsWithValidPropFromTreeData(woCountByLocationDisplayData, 'workOrderCounts')
				.map((el) => ({
					...el,
					subTotal: parseFloat(el.workOrderCounts.subTotal),
					percentageOfTotal: parseFloat(el.workOrderCounts.percentageOfTotal),
				}))
				.sort((a, b) => parseFloat(a.subTotal) - parseFloat(b.subTotal)),
		[woCountByLocationDisplayData]
	);

	const workOrderCountByLocationChartRows = useMemo(() => {
		const transformed = workOrderCountByLocationChartConfig.transformer(
			workOrderCountByLocationData
		);
		const rows = transformed.rows || workOrderCountByLocationData;
		return rows.map((row) => ({
			...row,
			fieldName: row.name || row.fieldName,
		}));
	}, [workOrderCountByLocationData]);

	const normalizeWorkLocationComparisonData = useCallback(
		(rawData) => {
			const localFilteredData = applyLocalAnalyticsTreeFilters(rawData || [], {
				selectedIds: locationIds,
				search,
			});
			const flatRows = collectObjectsWithValidPropFromTreeData(
				localFilteredData,
				'workOrderCounts'
			).map((row) => ({
				name: row.name,
				fieldName: row.name,
				subTotal: parseFloat(row.workOrderCounts.subTotal),
				percentageOfTotal: parseFloat(row.workOrderCounts.percentageOfTotal),
			}));
			const transformed = workOrderCountByLocationChartConfig.transformer(flatRows);
			return (transformed.rows || flatRows).map((row) => ({
				...row,
				fieldName: row.name || row.fieldName,
			}));
		},
		[locationIds, search]
	);

	const comparisonLocalFilterRevision = useMemo(
		() => `${search}|${locationIds.join(',')}`,
		[search, locationIds]
	);

	const workOrderCountByLocationColumns = useMemo(
		() => [
			{
				title: 'Location',
				dataIndex: 'name',
				key: 'name',
				render: (text) => (
					<Ellipsis tooltip={true} style={{ display: 'inline' }} length={28}>
						{text}
					</Ellipsis>
				),
			},
			{
				title: '# Work Orders',
				dataIndex: ['workOrderCounts', 'subTotal'],
				key: 'totalSpend',
				defaultSortOrder: defaultSortOrder,
				sorter: (a, b) =>
					nullSafeGetOrElse('workOrderCounts.subTotal', a, 0) -
					nullSafeGetOrElse('workOrderCounts.subTotal', b, 0),
				render: (amount) => amount,
			},
			{
				title: '% of Total',
				dataIndex: ['workOrderCounts', 'percentageOfTotal'],
				key: 'percentageOfTotal',
				render: (num) => floatToPercentageString((num || 0) / 100),
				width: 100,
			},
			{
				title: '',
				render: (data) => {
					const filters = {
						...locationFilters,
						locationIds: getObjectChildrenKeyIds(data),
					};
					return (
						<Link
							to={getSpendAnalyticsDrillDownLink(
								userType,
								getFilterIds(filters, FILTER_CONFIG_NAMES.CREATED_AT),
								'workOrders'
							)}
							target="_blank"
						>
							<Button type={'link'}>
								See Details <i className="icons8-font icons8-external-link ml-2 inline-block" />
							</Button>
						</Link>
					);
				},
				width: 100,
			},
		],
		[locationFilters]
	);

	return (
		<Content
			className="analyticsOverviewLocationsPage"
			style={{ padding: '0 0.5em', position: 'relative' }}
		>
			{/*<BackTop/>*/}
			{accountActivated ? (
				[
					<Row key={1} style={{ margin: '0.5em -8px' }} gutter={16}>
						<Col span={24}>
							<div
								style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
							>
								<div></div>
								<AnalyticsControlBar
									updateFilters={removeLocationIdsIdsAndUpdateFilter}
									clearFilters={clearAllLocationFilters}
									supplierFacilityIds={locationFilters.supplierFacilityIds}
									locationIds={locationIds}
									brandIds={locationFilters.brandIds}
									startDate={locationFilters.startDate}
									endDate={locationFilters.endDate}
									isPM={locationFilters.isPM}
									woPriorityIds={locationFilters.woPriorityIds}
									problemTypeIds={locationFilters.problemTypeIds}
									showSearch
									search={search}
									onSearch={onSearch}
									showPrint={true}
									onPrint={handlePrint}
									filterConfig={{
										workCompletedAt: true,
										problemTypes: true,
										workOrderPriorities: true,
										isPM: true,
										brandIds: true,
									}}
								/>
							</div>
						</Col>
					</Row>,
					<>
						<Row key={2} style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								<Card>
									<Spin spinning={workOrderCountByLocation.loading || initialLoading}>
										{woCountByLocationDisplayData.length > 0 ? (
											<Table
												columns={workOrderCountByLocationColumns}
												rowKey={(el) => el.id}
												dataSource={removeEmptyChildrenFromTreeData(woCountByLocationDisplayData)}
												expandable={{
													...(!!search && {
														expandedRowKeys: collectAllParentsFromTreeData(
															filteredTreeData,
															(_) => _.id
														),
													}),
												}}
											/>
										) : (
											<EmptyState
												height={120}
												headline={'No work orders found'}
												body={
													<div style={{ maxWidth: 488, textAlign: 'center' }}>
														We didn't find any work orders. Try changing your filters or expanding
														the selected time period.
													</div>
												}
											/>
										)}
									</Spin>
								</Card>
							</Col>
						</Row>
						<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
							<Col span={24}>
								{workOrderCountByLocationChartRows &&
								workOrderCountByLocationChartRows.length > 0 ? (
									<AnalyticsBreakdownChartSection
										loading={workOrderCountByLocation.loading || initialLoading}
										filters={locationFilters}
										userType={userType}
										primaryChartRows={workOrderCountByLocationChartRows}
										categoryField="name"
										fetchComparisonData={(params) =>
											fetchWorkBreakdownComparisonSilent(
												'work_order_counts_by_location_with_regions'
											)(userType)(params)
										}
										normalizeComparisonData={normalizeWorkLocationComparisonData}
										localFilterRevision={comparisonLocalFilterRevision}
										cols={workOrderCountByLocationChartConfig.cols}
										formatValueLabel={(val) => String(val)}
										valueAxisTitle="# Work Orders"
										exportTitle={CSV_TITLE}
										exportCategoryLabel="Location"
										exportValueLabel="Total Workorders"
										formatExportValue={(amount) => amount}
										singleTooltipItemTemplate={tooltipTemplate}
										chartTitleLabel="Work Orders by Location"
										chartTitleValue={`${workOrderCountByLocationChartRows.reduce(
											(sum, row) => sum + (parseFloat(row.subTotal) || 0),
											0
										)} Work Orders`}
									/>
								) : null}
							</Col>
						</Row>
					</>,
				]
			) : (
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
							alt="You haven't activated your account yet."
						/>
					}
					headline={"You haven't activated your account yet."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 560, marginBottom: 16 }}>
								Please check your email. We sent you a note with an activation link.
							</div>
						</div>
					}
				/>
			)}
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	accountActivated: state.session.accountActivated,
	workOrderPriorities: state.work_order_priorities,
	currentUser: state.session.currentUser,
	analytics: state.work_analytics,
	userType: (state as any).session.userType,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	saveLocationFilters: (filters) => dispatch(updateWorkLocationFilters(filters)),
	clearAllLocationFilters: () => dispatch(clearWorkLocationFilters()),
	fetchWorkOrderPriorities: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? workOrderPrioritiesRestCrudThunksForSupplier.read(params, filters, pagination, sorting)
				: workOrderPrioritiesRestCrudThunksForBuyer.read(params, filters, pagination, sorting)
		),
	fetchWorkOrderCountsByLocation: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(
			fetchWorkOrderCountByLocationWithRegions(ownProps.userType)(
				params,
				filters,
				pagination,
				sorting
			)
		),
	fetchWorkOrderCountsByMonth: (params, filters = null, pagination = null, sorting = null) =>
		dispatch(fetchWorkOrderCountByMonth(ownProps.userType)(params, filters, pagination, sorting)),
});

export default withUserType(
	withRouter(connect(mapStateToProps, mapDispatchToProps)(WorkAnalyticsOverviewLocationsPage))
);
